import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { BACKEND_CONFIG } from '../config/backendConfig';

interface CalendarEvent {
  id: number;
  title: string;
  description?: string;
  start_date: string;
  end_date: string;
  color: string;
  created_by: string;
  created_by_name?: string;
  created_by_email?: string;
  created_at: string;
  updated_at: string;
}

interface CalendarContextType {
  events: CalendarEvent[];
  loading: boolean;
  fetchEvents: (startDate?: string, endDate?: string) => Promise<void>;
  createEvent: (event: Partial<CalendarEvent>) => Promise<void>;
  updateEvent: (id: number, event: Partial<CalendarEvent>) => Promise<void>;
  deleteEvent: (id: number) => Promise<void>;
}

const CalendarContext = createContext<CalendarContextType | undefined>(undefined);

export const CalendarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { sessionToken } = useAuth();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchEvents = useCallback(async (startDate?: string, endDate?: string) => {
    if (!sessionToken) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const response = await fetch(
        `${BACKEND_CONFIG.baseURL}/calendar/events?${params}`,
        { headers: { Authorization: `Bearer ${sessionToken}` } }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }
      
      const data = await response.json();
      if (data.success) {
        setEvents(data.events);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
      // Don't show toast here - components can check loading state
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  const createEvent = useCallback(async (event: Partial<CalendarEvent>) => {
    if (!sessionToken) return;
    
    try {
      const response = await fetch(`${BACKEND_CONFIG.baseURL}/calendar/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionToken}`
        },
        body: JSON.stringify(event)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create event');
      }
      
      // Don't show toast here - let the component handle it
      await fetchEvents();
    } catch (error) {
      console.error('Failed to create event:', error);
      throw error;
    }
  }, [sessionToken, fetchEvents]);

  const updateEvent = useCallback(async (id: number, event: Partial<CalendarEvent>) => {
    if (!sessionToken) return;
    
    try {
      const response = await fetch(`${BACKEND_CONFIG.baseURL}/calendar/events/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionToken}`
        },
        body: JSON.stringify(event)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to update event');
      }
      
      // Don't show toast here - let the component handle it
      await fetchEvents();
    } catch (error) {
      console.error('Failed to update event:', error);
      throw error;
    }
  }, [sessionToken, fetchEvents]);

  const deleteEvent = useCallback(async (id: number) => {
    if (!sessionToken) return;
    
    try {
      const response = await fetch(`${BACKEND_CONFIG.baseURL}/calendar/events/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete event');
      }
      
      // Don't show toast here - let the component handle it
      await fetchEvents();
    } catch (error) {
      console.error('Failed to delete event:', error);
      throw error;
    }
  }, [sessionToken, fetchEvents]);

  return (
    <CalendarContext.Provider value={{ events, loading, fetchEvents, createEvent, updateEvent, deleteEvent }}>
      {children}
    </CalendarContext.Provider>
  );
};

export const useCalendar = () => {
  const context = useContext(CalendarContext);
  if (!context) {
    throw new Error('useCalendar must be used within CalendarProvider');
  }
  return context;
};
